import { useEffect, useRef, useState } from "react";
import { Plus, Edit, Trash2, X } from "lucide-react";
import api, { mediaUrl } from "@/lib/api";
import RichTextEditor from "@/components/admin/RichTextEditor";
import MediaPicker from "@/components/admin/MediaPicker";

const inputCls = "w-full border border-lp-border rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-lp-cyan";
const blank = { title: "", excerpt: "", content: "", category: "Insights", tags: "", cover_image: "", read_time: "5 min read", status: "draft", meta_title: "", meta_description: "" };

export default function BlogManager() {
  const [posts, setPosts] = useState([]);
  const [form, setForm] = useState(null);
  const [picker, setPicker] = useState(null); // 'cover' | 'rte'
  const insertRef = useRef(null);

  const load = () => api.get("/admin/blog").then((r) => setPosts(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.title.trim()) return;
    const payload = { ...form, tags: typeof form.tags === "string" ? form.tags.split(",").map((t) => t.trim()).filter(Boolean) : form.tags };
    try {
      if (form.id) await api.put(`/admin/blog/${form.id}`, payload);
      else await api.post("/admin/blog", payload);
      setForm(null); load();
    } catch (e) { alert("Save failed: " + (e.response?.data?.detail || e.message)); }
  };
  const del = async (id) => { if (window.confirm("Delete post?")) { await api.delete(`/admin/blog/${id}`); load(); } };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3"><h1 className="font-heading font-700 text-2xl text-lp-blue">Blog Posts</h1><span className="text-sm text-lp-muted bg-white border border-lp-border rounded-sm px-2.5 py-0.5">{posts.length}</span></div>
        <button onClick={() => setForm({ ...blank })} data-testid="admin-add-post" className="inline-flex items-center gap-2 bg-lp-blue text-white font-600 px-4 py-2.5 rounded-sm text-sm"><Plus size={15} /> New Post</button>
      </div>

      <div className="bg-white border border-lp-border rounded-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-lp-grey text-left"><tr><th className="px-4 py-3 font-600">Title</th><th className="px-4 py-3 font-600">Category</th><th className="px-4 py-3 font-600">Status</th><th className="px-4 py-3 font-600">Updated</th><th className="px-4 py-3">Actions</th></tr></thead>
          <tbody>
            {posts.map((p) => (
              <tr key={p.id} className="border-t border-lp-border hover:bg-lp-grey/50">
                <td className="px-4 py-3 font-600 text-lp-ink max-w-xs truncate">{p.title}</td>
                <td className="px-4 py-3 text-lp-muted">{p.category}</td>
                <td className="px-4 py-3"><span className={`text-xs font-600 px-2 py-1 rounded-sm ${p.status === "published" ? "bg-lp-cyan/15 text-lp-blue" : "bg-lp-grey text-lp-muted"}`}>{p.status}</span></td>
                <td className="px-4 py-3 text-lp-muted">{new Date(p.updated_at || p.created_at).toLocaleDateString("en-IN")}</td>
                <td className="px-4 py-3 flex gap-2"><button onClick={() => setForm({ ...p, tags: (p.tags || []).join(", ") })} className="text-lp-blue"><Edit size={16} /></button><button onClick={() => del(p.id)} className="text-destructive"><Trash2 size={16} /></button></td>
              </tr>
            ))}
            {posts.length === 0 && <tr><td colSpan={5} className="px-4 py-10 text-center text-lp-muted">No posts yet.</td></tr>}
          </tbody>
        </table>
      </div>

      {form && (
        <div className="fixed inset-0 z-50 bg-lp-ink/50 flex items-center justify-center p-4 overflow-y-auto" onClick={() => setForm(null)}>
          <div className="bg-white rounded-sm max-w-2xl w-full p-8 space-y-3 my-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between"><h3 className="font-heading font-700 text-xl text-lp-blue">{form.id ? "Edit" : "New"} Post</h3><button onClick={() => setForm(null)}><X size={20} className="text-lp-muted" /></button></div>
            <input placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={inputCls} data-testid="post-title-input" />
            <div className="grid grid-cols-2 gap-3">
              <input placeholder="Category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className={inputCls} />
              <input placeholder="Read time" value={form.read_time} onChange={(e) => setForm({ ...form, read_time: e.target.value })} className={inputCls} />
            </div>
            <div>
              <label className="text-xs font-600 text-lp-ink">Cover Image</label>
              <div className="flex items-center gap-3 mt-1">
                {form.cover_image && <img src={mediaUrl(form.cover_image)} alt="" className="h-12 w-12 object-cover rounded-sm border border-lp-border" />}
                <input placeholder="Cover image URL" value={form.cover_image} onChange={(e) => setForm({ ...form, cover_image: e.target.value })} className={inputCls} />
                <button type="button" onClick={() => setPicker("cover")} className="shrink-0 border border-lp-blue text-lp-blue font-600 px-3 py-2 rounded-sm text-sm">Library</button>
              </div>
            </div>
            <input placeholder="Tags (comma separated)" value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} className={inputCls} />
            <textarea placeholder="Excerpt" rows={2} value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} className={inputCls} />
            <div>
              <label className="text-xs font-600 text-lp-ink">Content</label>
              <div className="mt-1"><RichTextEditor value={form.content} onChange={(html) => setForm((prev) => ({ ...prev, content: html }))} insertRef={insertRef} onOpenLibrary={() => setPicker("rte")} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <input placeholder="Meta title (SEO)" value={form.meta_title} onChange={(e) => setForm({ ...form, meta_title: e.target.value })} className={inputCls} />
              <select data-testid="post-status-select" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className={inputCls}><option value="draft">Draft</option><option value="published">Published</option></select>
            </div>
            <textarea placeholder="Meta description (SEO)" rows={2} value={form.meta_description} onChange={(e) => setForm({ ...form, meta_description: e.target.value })} className={inputCls} />
            <div className="flex gap-3 pt-2"><button onClick={save} data-testid="post-save" className="bg-lp-blue text-white font-600 px-6 py-2.5 rounded-sm">Save Post</button><button onClick={() => setForm(null)} className="border border-lp-border px-6 py-2.5 rounded-sm">Cancel</button></div>
          </div>
        </div>
      )}

      <MediaPicker open={!!picker} onClose={() => setPicker(null)} onSelect={(url) => {
        if (picker === "cover") setForm((prev) => ({ ...prev, cover_image: url }));
        else if (picker === "rte" && insertRef.current) insertRef.current(url);
      }} />
    </div>
  );
}
